import { type ComponentType, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { Redirect } from '../../router/react-router-v5-compat';
import { HookDecorator } from '../main/main-page-states';
import CallServicePage from './call-service.page';
import FirstScreenPage from './first-screen.page';
import SelfOrderCommonSettingPage from './self-order-common-setting.page';
import KwaSettingPage from './kwa-setting.page';
import RwaSettingPage from './rwa-setting.page';
import QopSettingPage from './qop-setting.page';
import DisSettingPage from './dis-setting.page';
import MopSettingPage from './mop-setting.page';
import CopSettingPage from './cop-setting.page';
import {
    RbpCloudProjectSettingToolbar,
    SUB_FUNCTION_BASE,
    SUB_FUNCTION_FIRST_SCREEN_PATH,
} from './rbp-cloud-project-setting-toolbar';
import './scss/rbp-cloud-sub-function.page.scss';
import { RbpCloudSubFunctionAuxiliaryModeContext } from './rbp-cloud-sub-function-auxiliary-mode-context';

const TAB_DEFS: {
    /** 完整 hash 路径（不含 #） */
    path: string;
    /** 顶栏 Tab 文案 i18n key（与页面内主标题 key 分离，如 QOP Tab 与 `qop_setting_page_heading`） */
    i18nKey: string;
    /** 子页组件 */
    Component: ComponentType;
}[] = [
    { path: `${SUB_FUNCTION_BASE}/self-order`, i18nKey: 'self_order_setting_page_label', Component: SelfOrderCommonSettingPage },
    { path: SUB_FUNCTION_FIRST_SCREEN_PATH, i18nKey: 'first_screen_setting_page_label', Component: FirstScreenPage },
    { path: `${SUB_FUNCTION_BASE}/kwa`, i18nKey: 'kwa_setting_page_label', Component: KwaSettingPage },
    { path: `${SUB_FUNCTION_BASE}/rwa`, i18nKey: 'rwa_setting_page_label', Component: RwaSettingPage },
    { path: `${SUB_FUNCTION_BASE}/mop`, i18nKey: 'mop_setting_page_label', Component: MopSettingPage },
    { path: `${SUB_FUNCTION_BASE}/cop`, i18nKey: 'cop_setting_page_label', Component: CopSettingPage },
    { path: `${SUB_FUNCTION_BASE}/qop`, i18nKey: 'qop_setting_page_label', Component: QopSettingPage },
    { path: `${SUB_FUNCTION_BASE}/dis`, i18nKey: 'dis_setting_page_label', Component: DisSettingPage },
    { path: `${SUB_FUNCTION_BASE}/call-service`, i18nKey: 'call_service_setting_page_label', Component: CallServicePage },
];

const DEFAULT_TAB_PATH = TAB_DEFS[0].path;

/**
 * 云 RBP「子功能管理」壳页：顶部 Tab 对应原 RBP2 子功能二级菜单；内容区渲染各子页（对齐 Figma node 1838:51547 Tab 样式）。
 * 隧道与当前餐厅校验仅在本父级通过 `HookDecorator` 执行一次，子 Tab 页不再挂 `CurrentRestaurantDecorator`（与「我的餐厅」壳页一致）。
 */
export default HookDecorator(function RbpCloudSubFunctionPage() {
    const { pathname } = useLocation();
    /** 顶栏「辅助模式」开关：开启后子页展示 Figma 说明文案 */
    const [auxiliaryModeEnabled, setAuxiliaryModeEnabled] = useState(false);
    const auxiliaryModeContextValue = useMemo(
        () => ({ auxiliaryModeEnabled, setAuxiliaryModeEnabled }),
        [auxiliaryModeEnabled],
    );

    if (pathname === SUB_FUNCTION_BASE || pathname === `${SUB_FUNCTION_BASE}/`) {
        return <Redirect to={DEFAULT_TAB_PATH} />;
    }
    const active = TAB_DEFS.find((t) => t.path === pathname);
    if (!active) {
        return <Redirect to={DEFAULT_TAB_PATH} />;
    }

    const { Component } = active;

    return (
        <RbpCloudSubFunctionAuxiliaryModeContext.Provider value={auxiliaryModeContextValue}>
            <Box className="rbp-cloud-sub-function-page">
                <RbpCloudProjectSettingToolbar />
                <Box className="rbp-cloud-sub-function-page__panel">
                    <Component />
                </Box>
            </Box>
        </RbpCloudSubFunctionAuxiliaryModeContext.Provider>
    );
});
